import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";

const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="Terms of Service — KEISH Design Studio"
        description="Terms of Service for KEISH Design Studio. Covers design services, digital template purchases, licensing, and more."
      />
      <div className="min-h-screen bg-background text-foreground">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
          {/* Back link */}
           <Link
             to="/"
             className="inline-flex items-center gap-2 font-body text-ts-2xs tracking-[0.1em] uppercase text-muted-foreground hover:text-foreground transition-colors mb-12"
           >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Home
          </Link>

          <h1 className="font-display italic text-4xl md:text-5xl tracking-[-0.02em] mb-4">
            Terms of Service
          </h1>
           <p className="font-body text-ts-2xs tracking-[0.1em] uppercase text-muted-foreground mb-12">
             Last Updated: February 13, 2026
           </p>

           <div className="space-y-10 font-body text-ts-xs md:text-ts-sm leading-relaxed text-foreground/80">
            {/* Intro */}
            <section>
              <p>
                These Terms of Service ("Terms") govern your use of the website{" "}
                <strong className="text-foreground">keish.studio</strong> and all related services, digital
                products, and templates provided by KEISH Design Studio ("we," "us," or "our"), operated by
                Madi Green.
              </p>
              <p className="mt-3">
                By accessing our website or purchasing our products and services, you agree to be bound by
                these Terms. If you do not agree, please do not use our site or services.
              </p>
            </section>

            {/* Services */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                1. Design Services
              </h2>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>
                  <strong className="text-foreground">Scope of Work:</strong> All design projects are scoped
                  through a discovery process. Deliverables, timelines, and pricing are agreed upon before
                  work begins and outlined in a project proposal or contract.
                </li>
                <li>
                  <strong className="text-foreground">Revisions:</strong> Revision rounds are specified in
                  your project agreement. Additional revisions beyond the agreed scope may incur extra fees.
                </li>
                <li>
                  <strong className="text-foreground">Client Responsibilities:</strong> Clients are responsible
                  for providing content, feedback, and approvals in a timely manner. Delays caused by the client
                  may affect project timelines.
                </li>
                <li>
                  <strong className="text-foreground">Approval & Launch:</strong> Final approval from the client
                  is required before any work is published or launched. Once approved, the client assumes
                  responsibility for the live deliverable.
                </li>
              </ul>
            </section>

            {/* Digital Products */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                2. Digital Products & Templates
              </h2>

               <h3 className="font-body text-ts-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground mt-6 mb-3">
                 Purchases
               </h3>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Digital templates and products are sold through third-party platforms linked from our website</li>
                <li>All prices are listed in USD unless otherwise noted</li>
                <li>Payment is processed securely by the respective platform (e.g., Stripe, Gumroad, Lemon Squeezy)</li>
                <li>You will receive access to your purchased product via email or the platform's delivery system</li>
              </ul>

               <h3 className="font-body text-ts-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground mt-6 mb-3">
                 License & Usage
               </h3>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>
                  Each template purchase grants you a <strong className="text-foreground">single-use, non-exclusive license</strong> to
                  use the template for one personal or commercial project
                </li>
                <li>You may customize the template for your own use</li>
                <li>
                  You may <strong className="text-foreground">not</strong> resell, redistribute, sublicense,
                  or share the template files with others
                </li>
                <li>
                  You may <strong className="text-foreground">not</strong> claim the template design as your own
                  original work or use it to create a competing product
                </li>
                <li>Multi-use or extended licenses may be available upon request — contact us for details</li>
              </ul>

               <h3 className="font-body text-ts-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground mt-6 mb-3">
                 Refunds
               </h3>
              <p>
                Due to the digital nature of our products, <strong className="text-foreground">all sales are final</strong>.
                We do not offer refunds or exchanges on digital template purchases. If you experience a technical issue
                with a product, please contact us at{" "}
                <a href="mailto:madi@keish.studio" className="underline hover:text-foreground transition-colors">
                  madi@keish.studio
                </a>{" "}
                and we will do our best to resolve it.
              </p>
            </section>

            {/* Payment */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                3. Payment Terms
              </h2>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>
                  <strong className="text-foreground">Design Services:</strong> Payment terms are outlined in
                  your project proposal. Typically, a deposit is required before work begins, with the remaining
                  balance due upon project completion or at agreed milestones.
                </li>
                <li>
                  <strong className="text-foreground">Digital Products:</strong> Full payment is required at
                  the time of purchase.
                </li>
                <li>
                  <strong className="text-foreground">Late Payments:</strong> Overdue invoices for design
                  services may incur a late fee. Continued non-payment may result in project suspension.
                </li>
              </ul>
            </section>

            {/* IP */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                4. Intellectual Property
              </h2>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>
                  All content on keish.studio — including designs, copy, graphics, logos, and code — is the
                  intellectual property of KEISH Design Studio unless otherwise stated
                </li>
                <li>
                  For client projects, intellectual property rights transfer to the client upon final payment
                  as specified in the project agreement
                </li>
                <li>
                  KEISH Design Studio retains the right to display completed work in its portfolio and
                  marketing materials unless otherwise agreed in writing
                </li>
                <li>
                  You may not reproduce, distribute, or create derivative works from our website content
                  without express written permission
                </li>
              </ul>
            </section>

            {/* Website Use */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                5. Website Use
              </h2>
              <p className="mb-3">When using our website, you agree not to:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Use the site for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to any part of the site or its systems</li>
                <li>Scrape, crawl, or harvest data from the site without permission</li>
                <li>Interfere with the site's functionality or security</li>
                <li>Upload or transmit malicious code</li>
              </ul>
            </section>

            {/* Third-Party */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                6. Third-Party Services & Links
              </h2>
              <p>
                Our website contains links to third-party platforms for product sales, social media, and
                external client work. We are not responsible for the content, policies, or practices of these
                third-party sites. Your interactions with third-party platforms are governed by their
                respective terms and privacy policies.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                7. Limitation of Liability
              </h2>
              <p>
                KEISH Design Studio provides its website and services "as is" without warranties of any kind,
                express or implied. To the fullest extent permitted by law:
              </p>
              <ul className="list-disc pl-5 space-y-1.5 mt-3">
                <li>
                  We are not liable for any indirect, incidental, consequential, or punitive damages arising
                  from your use of our website, services, or products
                </li>
                <li>
                  Our total liability for any claim shall not exceed the amount you paid for the specific
                  product or service giving rise to the claim
                </li>
                <li>
                  We do not guarantee uninterrupted or error-free operation of our website
                </li>
              </ul>
            </section>

            {/* Indemnification */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                8. Indemnification
              </h2>
              <p>
                You agree to indemnify and hold harmless KEISH Design Studio and its owner from any claims,
                damages, losses, or expenses (including legal fees) arising from your use of our website,
                violation of these Terms, or infringement of any third-party rights.
              </p>
            </section>

            {/* Termination */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                9. Termination
              </h2>
              <p>
                We reserve the right to suspend or terminate your access to our website or services at our
                discretion, without notice, for conduct that violates these Terms or is harmful to other
                users, us, or third parties. For design service agreements, termination terms are specified
                in the individual project contract.
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                10. Governing Law
              </h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of
                Texas, United States, without regard to conflict of law provisions. Any disputes shall be
                resolved in the courts of Dallas County, Texas.
              </p>
            </section>

            {/* Locals Only Club */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                11. Locals Only Club — Promotional Offers
              </h2>
              <p className="mb-3">
                KEISH Design Studio may, from time to time, offer promotional pricing, discounted rates, flat-rate packages, and/or barter/trade agreements under the "Locals Only Club" program (collectively, "Promotional Offers"). By participating in any Promotional Offer, you acknowledge and agree to the following:
              </p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>
                  <strong className="text-foreground">Eligibility:</strong> Promotional Offers are available exclusively to individuals and businesses located in the Dallas–Fort Worth metropolitan area. KEISH Design Studio reserves the right to verify eligibility and deny participation at its sole discretion.
                </li>
                <li>
                  <strong className="text-foreground">Availability:</strong> All Promotional Offers are subject to availability, capacity, and scheduling. Submission of an inquiry does not guarantee acceptance or participation.
                </li>
                <li>
                  <strong className="text-foreground">Pricing & Scope:</strong> Promotional rates (including but not limited to percentage discounts and flat-rate packages) apply only to the scope of work outlined in the corresponding offer description. Any work exceeding the agreed scope will be billed at standard rates.
                </li>
                <li>
                  <strong className="text-foreground">Barter & Trade:</strong> Barter or trade agreements are evaluated on a case-by-case basis. Acceptance is not guaranteed. Both parties must agree in writing to the terms of the exchange before work commences. KEISH Design Studio reserves the right to decline any proposed trade for any reason.
                </li>
                <li>
                  <strong className="text-foreground">No Combination:</strong> Promotional Offers may not be combined with other discounts, promotions, or special pricing unless explicitly stated in writing.
                </li>
                <li>
                  <strong className="text-foreground">Modification & Termination:</strong> KEISH Design Studio reserves the right to modify, suspend, or discontinue any Promotional Offer at any time, without prior notice and without liability. Changes will not affect projects already under a signed agreement.
                </li>
                <li>
                  <strong className="text-foreground">Not a Binding Offer:</strong> Marketing materials, website descriptions, and promotional language related to the Locals Only Club are for informational purposes only and do not constitute a binding contract, guarantee of pricing, or obligation to perform services.
                </li>
                <li>
                  <strong className="text-foreground">Governing Agreement:</strong> All accepted projects under a Promotional Offer will be governed by a separate signed project agreement, which will supersede any general promotional language. All other sections of these Terms of Service remain in full effect.
                </li>
              </ul>
            </section>

            {/* Changes */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                12. Changes to These Terms
              </h2>
              <p>
                We may update these Terms from time to time. Changes will be posted on this page with an
                updated "Last Updated" date. Continued use of our website and services after any changes
                constitutes acceptance of the revised Terms.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                13. Contact Us
              </h2>
              <p>
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="mt-3 p-5 border border-border rounded-sm bg-muted/30">
                <p className="font-semibold text-foreground">KEISH Design Studio</p>
                <p className="mt-1">
                  Email:{" "}
                  <a href="mailto:madi@keish.studio" className="underline hover:text-foreground transition-colors">
                    madi@keish.studio
                  </a>
                </p>
                <p className="mt-1">
                  Website:{" "}
                  <a href="https://keish.studio" className="underline hover:text-foreground transition-colors">
                    keish.studio
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default Terms;
